import { endpoints } from "../../../configs/Settings";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useAuth } from "../../../utils/useAuth";
import { useAxios } from "../../../utils/useAxios";

export type AppointmentStatusType =
  | "proposed"
  | "pending"
  | "booked"
  | "arrived"
  | "fulfilled"
  | "cancelled"
  | "noshow"
  | "entered-in-error"
  | "checked-in"
  | "waitlist";

interface IProps {
  count?: number;
  /**
   * ID do Patient (sem prefixo "Patient/").
   * Quando informado, restringe a busca ao paciente específico.
   *
   * Motivação: permitir checagens booleanas do tipo "este paciente já tem
   * appointments nas locations X?" sem precisar varrer todos os pacientes.
   */
  patientId?: string;
  locationActors?: string;
  practitionerActor?: string;
  purposeofuse?: string;
  status?: AppointmentStatusType[];
  /** Indica se deve retornar apenas o resumo */
  startDate?: string;
  endDate?: string;
  summary?: boolean;
}

/**
 * Obtém pacientes com base em seus agendamentos
 *
 * @param count número de resultados a serem retornados (padrão: 10000)
 * @param locationActors string com IDs das localizações, separados por vírgula
 * @param practitionerActor string com ID do profissional
 * @param status array com status dos agendamentos a serem filtrados
 * @param summary indica se deve retornar apenas o resumo
 * @returns Bundle contendo os pacientes
 */
export async function PatientBasedAppointment({
  count = 10000,
  patientId,
  locationActors,
  practitionerActor,
  purposeofuse,
  status,
  summary,
  startDate,
  endDate,
}: IProps) {
  // Construir URL base
  let url = `${endpoints.SCHEDULING}/Patient`;

  // Construir parâmetros de query
  const queryParams: string[] = [];

  // Adicionar parâmetros válidos ao array
  queryParams.push(`_count=${count}`);

  // Restringe ao paciente específico (mudança compatível: só aplica quando houver patientId)
  if (patientId) {
    queryParams.push(`_id=${encodeURIComponent(patientId)}`);
  }

  // Adiciona filtro de relacionamento com Appointment por profissional
  if (practitionerActor) {
    queryParams.push(`_has:Appointment:patient:actor=${practitionerActor}`);
  }

  // Adiciona filtro de relacionamento com Appointment por localização
  if (locationActors) {
    queryParams.push(`_has:Appointment:patient:actor=${locationActors}`);
  }

  // Adiciona filtro por status, se fornecido
  if (status && status.length > 0) {
    queryParams.push(`_has:Appointment:patient:status=${status.join(",")}`);
  }

  // Adiciona filtros de data
  if (startDate) {
    queryParams.push(`_has:Appointment:patient:date=ge${startDate}`);
  }
  if (endDate) {
    queryParams.push(`_has:Appointment:patient:date=le${endDate}`);
  }

  // Adiciona parâmetro de resumo
  if (summary) {
    queryParams.push("_summary=count");
  }

  // Concatenar parâmetros com '&'
  url = `${url}?${queryParams.join("&")}`;

  const subjectId = await useAuth.getSubjectId();
  const organizationId = await useAuth.getOrganizationId();

  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    ...(subjectId && { "subject-id": subjectId }),
    ...(organizationId && { "organization-id": organizationId }),
    ...(purposeofuse && { purposeofuse: purposeofuse }),
  };

  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`PatientBasedAppointment: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("PatientBasedAppointment ", error);
      // toast.error(`PatientBasedAppointment Error: ${error?.response?.status}`);
      throw new Error();
    });

  if (response) return response;
}
